import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Container,
  Skeleton,
  // useTheme,
  // useMediaQuery
} from '@mui/material';
import {
  Star as StarIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { InventoryItem } from '../../utils/types';
import ProductCard from './ProductCard';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';

interface FeaturedProductsProps {
  title?: string;
  maxItems?: number;
}

const FeaturedProducts: React.FC<FeaturedProductsProps> = ({
  title = 'المنتجات المميزة',
  maxItems = 8
}) => {
  const { settings } = useStoreSettings();
  const [products, setProducts] = useState<InventoryItem[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // const { addToCart } = useCart(); // سيتم استخدامه في ProductCard

  // يمكن استخدام هذه المتغيرات لاحقاً لتحسين تجربة المستخدم على الأجهزة المختلفة
  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // تخزين مؤقت للمنتجات المميزة في localStorage
    const CACHE_KEY = 'featuredProducts';
    const CACHE_EXPIRY_KEY = 'featuredProductsExpiry';
    const CACHE_DURATION = 5 * 60 * 1000; // 5 دقائق بالمللي ثانية

    const fetchFeaturedProducts = async () => {
      try {
        setLoading(true);

        // التحقق من وجود بيانات مخزنة مؤقتًا وصالحة
        const cachedData = localStorage.getItem(CACHE_KEY);
        const cachedExpiry = localStorage.getItem(CACHE_EXPIRY_KEY);

        if (cachedData && cachedExpiry && Date.now() < parseInt(cachedExpiry)) {
          console.log('Using cached featured products');
          setProducts(JSON.parse(cachedData));
          setLoading(false);
          return;
        }

        console.log('Fetching featured products from Firestore...');

        // إنشاء استعلام مبسط للمنتجات المميزة
        const productsQuery = query(
          collection(db, 'inventory'),
          where('featured', '==', true),
          limit(maxItems * 2) // نحصل على ضعف العدد للفلترة المحلية
        );

        const querySnapshot = await getDocs(productsQuery);
        console.log(`Found ${querySnapshot.size} featured products`);

        if (querySnapshot.empty) {
          console.log('No featured products found');
          setProducts([]);
          setLoading(false);
          return;
        }

        // تحويل البيانات من Firestore إلى نموذج InventoryItem
        const featuredProducts = querySnapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.name || '',
              sku: data.sku || '',
              barcode: data.barcode || '',
              category: data.category || '',
              categoryId: data.categoryId || '',
              subcategory: data.subcategory || '',
              subcategoryId: data.subcategoryId || '',
              purchasePrice: data.purchasePrice || 0,
              sellingPrice: data.sellingPrice || 0,
              quantity: data.quantity || 0,
              minQuantity: data.minQuantity || 0,
              description: data.description || '',
              supplier: data.supplier || '',
              image: data.image || '',
              imageUrl: data.imageUrl || data.image || '',
              showInStore: data.showInStore !== undefined ? data.showInStore : true,
              featured: data.featured || false,
              bestSeller: data.bestSeller || false,
              createdAt: data.createdAt ? new Date(data.createdAt.seconds * 1000) : new Date(),
              updatedAt: data.updatedAt ? new Date(data.updatedAt.seconds * 1000) : new Date()
            } as InventoryItem;
          })
          .filter(product => {
            // فلترة محلية للمنتجات المعروضة في المتجر
            const showInStore = product.showInStore !== undefined ? product.showInStore : true;
            return showInStore === true;
          })
          .slice(0, maxItems); // تحديد العدد المطلوب بعد الفلترة

        // تخزين البيانات في localStorage مع وقت انتهاء الصلاحية
        localStorage.setItem(CACHE_KEY, JSON.stringify(featuredProducts));
        localStorage.setItem(CACHE_EXPIRY_KEY, (Date.now() + CACHE_DURATION).toString());

        setProducts(featuredProducts);
      } catch (error) {
        console.error('Error fetching featured products:', error);
        setError('حدث خطأ أثناء تحميل المنتجات المميزة');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();

    // تنظيف عند إزالة المكون
    return () => {
      console.log('Cleaning up featured products component');
    };
  }, [maxItems]);

  // تنسيق السعر تم نقله إلى ProductCard

  // إذا لم تكن هناك منتجات مميزة، لا نعرض أي شيء
  if (!loading && products.length === 0) {
    return null;
  }

  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
      <Box sx={{ mb: 6 }}>
        {/* عنوان القسم */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <StarIcon color="primary" sx={{ mr: 1 }} />
            <Typography
              variant="h4"
              component="h2"
              className="section-title"
              sx={{
                fontWeight: 'bold',
                position: 'relative',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  right: 0,
                  width: 60,
                  height: 4,
                  backgroundColor: 'primary.main',
                  borderRadius: 2
                }
              }}
            >
              {title}
            </Typography>
          </Box>

          <Button
            component={RouterLink}
            to="/products/featured"
            color="primary"
            sx={{
              fontWeight: 'bold',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: 'white',
                backgroundColor: 'primary.main'
              }
            }}
          >
            عرض الكل
          </Button>
        </Box>

        {/* شبكة المنتجات */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {loading
            ? Array.from(new Array(maxItems)).map((_, index) => {
                const desktopCols = settings?.display?.productCardColumns || 4;
                const mobileCols = settings?.display?.mobileProductColumns || 2;
                const xsVal = mobileCols === 1 ? 12 : 6;
                const smVal = desktopCols > 2 ? 6 : 12;
                const mdVal = desktopCols === 4 ? 4 : (desktopCols === 3 ? 4 : 6);
                const lgVal = 12 / desktopCols;
                return (
                  <Grid item xs={xsVal} sm={smVal} md={mdVal} lg={lgVal} key={`skeleton-${index}`}>
                    <Box sx={{ width: '100%', my: 1 }}>
                      <Skeleton variant="rectangular" width="100%" height={140} sx={{ borderRadius: 1 }} />
                      <Skeleton width="80%" height={24} sx={{ mt: 1 }} />
                      <Skeleton width="60%" height={20} sx={{ mt: 0.5 }} />
                      <Skeleton width="40%" height={30} sx={{ mt: 1 }} />
                    </Box>
                  </Grid>
                );
              })
            : products.map((product) => {
                const desktopCols = settings?.display?.productCardColumns || 4;
                const mobileCols = settings?.display?.mobileProductColumns || 2;
                const xsVal = mobileCols === 1 ? 12 : 6;
                const smVal = desktopCols > 2 ? 6 : 12;
                const mdVal = desktopCols === 4 ? 4 : (desktopCols === 3 ? 4 : 6);
                const lgVal = 12 / desktopCols;
                return (
                  <Grid item xs={xsVal} sm={smVal} md={mdVal} lg={lgVal} key={product.id}>
                    <ProductCard product={product} />
                  </Grid>
                );
              })}

        </Grid>

        {error && (
          <Typography color="error" align="center" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default FeaturedProducts;
